import React, { useState } from 'react';
import { X, Upload, CheckCircle, AlertCircle, Download, FileText } from 'lucide-react';
import { leadsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { message } from 'antd';

function BulkLeadUploadModal({ isOpen, onClose, onUploadSuccess }) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  if (!isOpen) return null;

  const handleDownloadTemplate = () => {
    // Create CSV template with exact field names from backend
    const templateData = [
      [
        'customerName',
        'contactNumber',
        'alternateNumber',
        'email',
        'gender',
        'age',
        'height',
        'weight',
        'actualProblem',
        'existingMedicalTreatment',
        'maritalStatus',
        'address',
        'areaPinCode',
        'postOffice',
        'district',
        'state',
        'source',
        'status',
        'remarks'
      ],

      [
        'John Doe',
        '9876543210',
        '9876543211',
        'john@example.com',
        'MALE',
        '35',
        '5.8',
        '70',
        'Back Pain',
        'None',
        'Married',
        '123 Main Street, New Colony',
        '110001',
        'Chhajarasi Post Office',
        'Delhi',
        'Delhi',
        'INSTAGRAM',
        'NEW_LEAD',
        'Sample lead from bulk upload'
      ],

      [
        'Jane Smith',
        '9876543212',
        '',
        'jane@example.com',
        'FEMALE',
        '28',
        '5.4',
        '55',
        'Joint Pain',
        'Physiotherapy',
        'Single',
        '456 Park Avenue, Green Park',
        '110002',
        'Post office near Shiv Mandir',
        'Delhi',
        'Delhi',
        'FACEBOOK',
        'NEW_LEAD',
        'Interested in consultation'
      ],
      [
        'Rajesh Kumar',
        '9876543213',
        '9876543214',
        'rajesh@example.com',
        'MALE',
        '42',
        '5.9',
        '75',
        'Arthritis',
        'Ayurvedic medicine',
        'Married',
        '789 Ring Road, Karol Bagh',
        '110005',
        'India Post Office',
        'Delhi',
        'Delhi',
        'JUST_DIAL',
        'NEW_LEAD',
        'Referred by friend'
      ]
    ];

    // Convert to CSV with proper escaping
    const csvContent = templateData.map(row =>
      row.map(cell => {
        // Escape cells that contain commas or quotes
        if (typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead_bulk_upload_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validExtensions = ['.csv', '.xlsx', '.xls'];
      const fileName = file.name.toLowerCase();
      const isValidType = validExtensions.some(ext => fileName.endsWith(ext));

      if (!isValidType) {
        message.warning('Please upload a valid CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }

      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        message.warning('File size must be less than 10MB');
        return;
      }

      setUploadedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv,.xlsx,.xls';
    input.onchange = handleFileChange;
    input.click();
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setUploadResult(null);
  };

  const enrichDuplicateLeadsWithDate = async (duplicates) => {
    if (!Array.isArray(duplicates) || duplicates.length === 0) return duplicates;
    return Promise.all(
      duplicates.map(async (dup) => {
        if (dup.createdAt || dup.created_at || dup.date) return dup;
        try {
          const idMatch = dup.reason?.match(/Lead ID:\s*(LD\d+|\d+)/i);
          if (idMatch) {
            const rawId = idMatch[1];
            const cleanId = rawId.replace(/^LD0*/i, '');
            const leadRes = await leadsApi.getLeadById(cleanId || rawId).catch(() => null);
            const date = leadRes?.createdAt || leadRes?.created_at || leadRes?.lead?.createdAt;
            if (date) return { ...dup, createdAt: date };
          }
          if (dup.contactNumber) {
            const searchRes = await leadsApi.searchLeads(dup.contactNumber).catch(() => null);
            const items = Array.isArray(searchRes)
              ? searchRes
              : searchRes?.items || searchRes?.content || searchRes?.leads || [];
            const found = items.find((l) => (l.contactNumber || l.phone) === dup.contactNumber) || items[0];
            const date = found?.createdAt || found?.created_at || found?.date;
            if (date) return { ...dup, createdAt: date };
          }
        } catch (e) {
          console.warn('Could not fetch existing lead date:', e);
        }
        return dup;
      })
    );
  };

  const handleSave = async () => {
    if (!uploadedFile) {
      message.warning('Please upload a file first');
      return;
    }

    try {
      setUploading(true);

      // Call the backend API
      const result = await leadsApi.bulkUploadLeads(uploadedFile);

      console.log('Upload result:', result);

      // Process the response from backend
      setUploadResult({
        success: result.success,
        message: result.success
          ? `Successfully uploaded ${result.successCount} out of ${result.totalProcessed} leads`
          : 'Upload completed with some errors',
        totalProcessed: result.totalProcessed || 0,
        successCount: result.successCount || 0,
        duplicateCount: result.duplicateCount || (result.duplicateLeads ? result.duplicateLeads.length : 0),
        failureCount: result.failureCount || 0,
        failedLeads: result.failedLeads || [],
        duplicateLeads: result.duplicateLeads || [],
        successfulLeads: result.successfulLeads || []
      });

      // Asynchronously fetch existing lead creation dates from database
      if (result.duplicateLeads && result.duplicateLeads.length > 0) {
        enrichDuplicateLeadsWithDate(result.duplicateLeads).then((enriched) => {
          setUploadResult((prev) => (prev ? { ...prev, duplicateLeads: enriched } : prev));
        });
      }

      // Notify parent component if upload was successful
      if (result.success && onUploadSuccess) {
        onUploadSuccess(result);
      }

      // Auto close after 3 seconds if no errors and no duplicates
      if (result.success && result.failureCount === 0 && (!result.duplicateCount || result.duplicateCount === 0)) {
        setTimeout(() => {
          handleClose();
        }, 3000);
      }

    } catch (error) {
      console.error('Error uploading leads:', error);
      setUploadResult({
        success: false,
        message: error.message || 'Failed to upload leads. Please try again.',
        totalProcessed: 0,
        successCount: 0,
        duplicateCount: 0,
        failureCount: 0,
        failedLeads: [{ row: '0', error: error.message || 'Network error occurred' }],
        duplicateLeads: []
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setUploadedFile(null);
    setUploadResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm transition-all duration-300">
      <div className={`rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border shadow-2xl transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-[#E8F5F0] border-teal-100'}`}>
        <div className={`sticky top-0 z-10 p-6 pb-4 border-b transition-all ${isDark ? 'bg-[#111827] border-white/10' : 'bg-[#E8F5F0] border-teal-200'}`}>
          {/* Header */}
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-black uppercase tracking-widest ${isDark ? 'text-white' : 'text-gray-800'}`}>Add bulk Lead</h2>
            <button
              onClick={handleClose}
              className={`p-1 rounded-full transition-all ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-200'}`}
            >
              <X className={`w-6 h-6 ${isDark ? 'text-white' : 'text-gray-600'}`} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Upload Result Alert */}
          {uploadResult && (
            <div className={`mb-6 p-4 rounded-lg border-2 ${uploadResult.success
              ? 'bg-green-50 border-green-300'
              : 'bg-red-50 border-red-300'
              }`}>
              <div className="flex items-start space-x-3">
                {uploadResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  <h4 className={`font-semibold mb-2 ${uploadResult.success ? 'text-green-800' : 'text-red-800'
                    }`}>
                    {uploadResult.message}
                  </h4>

                  {uploadResult.totalProcessed > 0 && (
                    <div className="grid grid-cols-4 gap-2.5 mb-3">
                      <div className={`rounded-xl p-3 border transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-white border-gray-200'}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</p>
                        <p className={`text-lg font-black ${isDark ? 'text-white' : 'text-gray-800'}`}>{uploadResult.totalProcessed}</p>
                      </div>
                      <div className={`rounded-xl p-3 border transition-all ${isDark ? 'bg-green-500/5 border-green-500/10' : 'bg-white border-green-200'}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-green-500/70' : 'text-green-600'}`}>Success</p>
                        <p className={`text-lg font-black ${isDark ? 'text-green-400' : 'text-green-600'}`}>{uploadResult.successCount}</p>
                      </div>
                      <div className={`rounded-xl p-3 border transition-all ${isDark ? 'bg-yellow-500/5 border-yellow-500/10' : 'bg-white border-yellow-200'}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-yellow-500/70' : 'text-yellow-600'}`}>Duplicates</p>
                        <p className={`text-lg font-black ${isDark ? 'text-yellow-400' : 'text-yellow-600'}`}>{uploadResult.duplicateCount || 0}</p>
                      </div>
                      <div className={`rounded-xl p-3 border transition-all ${isDark ? 'bg-red-500/5 border-red-500/10' : 'bg-white border-red-200'}`}>
                        <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${isDark ? 'text-red-500/70' : 'text-red-600'}`}>Failed</p>
                        <p className={`text-lg font-black ${isDark ? 'text-red-400' : 'text-red-600'}`}>{uploadResult.failureCount}</p>
                      </div>
                    </div>
                  )}

                  {/* Duplicate Leads Detail Table */}
                  {uploadResult.duplicateLeads && uploadResult.duplicateLeads.length > 0 && (
                    <div className="mt-3 mb-3">
                      <p className="text-sm font-semibold text-yellow-800 mb-2">
                        Duplicate Leads ({uploadResult.duplicateLeads.length}):
                      </p>
                      <div className="bg-white rounded-lg border border-yellow-200 max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-yellow-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2 font-semibold text-yellow-800">Row</th>
                              <th className="text-left p-2 font-semibold text-yellow-800">Customer</th>
                              <th className="text-left p-2 font-semibold text-yellow-800">Contact</th>
                              <th className="text-left p-2 font-semibold text-yellow-800">Reason</th>
                              <th className="text-left p-2 font-semibold text-yellow-800">Created At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.duplicateLeads.map((dup, index) => {
                              const dateVal = dup.createdAt || dup.created_at || dup.date || dup.createdDate || dup.leadCreatedAt || new Date();
                              const formattedDate = (() => {
                                try {
                                  const d = new Date(dateVal);
                                  if (isNaN(d.getTime())) return String(dateVal);
                                  const day = String(d.getDate()).padStart(2, '0');
                                  const month = String(d.getMonth() + 1).padStart(2, '0');
                                  const year = d.getFullYear();
                                  const hours = String(d.getHours()).padStart(2, '0');
                                  const minutes = String(d.getMinutes()).padStart(2, '0');
                                  return `${day}/${month}/${year}, ${hours}:${minutes}`;
                                } catch (e) {
                                  return String(dateVal);
                                }
                              })();

                              return (
                                <tr key={index} className="border-t border-yellow-100">
                                  <td className="p-2 text-gray-700 font-mono">{dup.row || index + 1}</td>
                                  <td className="p-2 text-gray-800 font-medium">{dup.customerName || '-'}</td>
                                  <td className="p-2 text-gray-700 font-mono">{dup.contactNumber || '-'}</td>
                                  <td className="p-2 text-yellow-700">{dup.reason || 'Already exists'}</td>
                                  <td className="p-2 text-gray-600 whitespace-nowrap">{formattedDate}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {uploadResult.failedLeads && uploadResult.failedLeads.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-semibold text-red-800 mb-2">
                        Failed Leads ({uploadResult.failedLeads.length}):
                      </p>
                      <div className="bg-white rounded-lg border border-red-200 max-h-48 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-red-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2 font-semibold text-red-800">Row</th>
                              <th className="text-left p-2 font-semibold text-red-800">Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {uploadResult.failedLeads.slice(0, 10).map((failed, index) => (
                              <tr key={index} className="border-t border-red-100">
                                <td className="p-2 text-gray-700">{failed.row}</td>
                                <td className="p-2 text-red-700">{failed.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {uploadResult.failedLeads.length > 10 && (
                          <div className="p-2 text-center text-xs text-red-600 bg-red-50 border-t border-red-100">
                            ...and {uploadResult.failedLeads.length - 10} more errors
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="space-y-6">
            {/* Step 1 - Download Template */}
            <div>
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm transition-all ${isDark ? 'bg-white/10 text-emerald-400' : 'bg-white text-teal-600'}`}>
                  <span className="font-black">1</span>
                </div>
                <div className="flex-1">
                  <h3 className={`font-black uppercase tracking-widest text-sm mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Please download Excel file template.
                  </h3>
                  <p className="text-xs font-medium text-gray-500 mb-4">
                    Choose and download the template and fill in your leads data.
                  </p>
                  <button
                    onClick={handleDownloadTemplate}
                    className={`px-6 py-3 border-2 rounded-xl transition-all flex items-center space-x-2 font-black text-[10px] uppercase tracking-widest shadow-lg active:scale-95 ${isDark ? 'bg-white/5 border-white/10 text-white hover:bg-white/10' : 'bg-white border-teal-500 text-teal-700 hover:bg-teal-50'}`}
                  >
                    <Download className="w-5 h-5" />
                    <span>Download Template</span>
                  </button>

                  {/* Template Info */}
                  <div className={`mt-4 p-4 rounded-xl border transition-all ${isDark ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-start space-x-3">
                      <FileText className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="text-[10px] font-black uppercase tracking-widest">
                        <p className={`mb-2 ${isDark ? 'text-blue-400' : 'text-blue-800'}`}>Template includes:</p>
                        <ul className={`list-disc list-inside space-y-1 ${isDark ? 'text-blue-400/70' : 'text-blue-700'}`}>
                          <li>Required fields: Customer Name, Contact Number</li>
                          <li>Optional fields: Email, Age, Address, etc.</li>
                          <li>Sample data rows for reference</li>
                          <li>Supports: CSV, XLSX, XLS formats</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 - Upload File */}
            <div>
              <div className="flex items-start space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm transition-all ${isDark ? 'bg-white/10 text-emerald-400' : 'bg-white text-teal-600'}`}>
                  <span className="font-black">2</span>
                </div>
                <div className="flex-1">
                  <h3 className={`font-black uppercase tracking-widest text-sm mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    Please upload updated Excel file template.
                  </h3>
                  <p className="text-xs font-medium text-gray-500 mb-4">
                    Please upload filled data here.
                  </p>

                  {/* Upload Area */}
                  {!uploadedFile ? (
                    <div
                      onClick={handleUpload}
                      className={`border-2 border-dashed rounded-2xl p-8 cursor-pointer transition-all ${isDark ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20' : 'bg-white border-teal-300 hover:border-teal-500 hover:bg-teal-50'}`}
                    >
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-all ${isDark ? 'bg-white/10 text-emerald-400' : 'bg-teal-50 text-teal-500'}`}>
                          <Upload className="w-8 h-8" />
                        </div>
                        <div className="text-center">
                          <button className={`font-black text-[10px] uppercase tracking-widest ${isDark ? 'text-emerald-400' : 'text-teal-600'}`}>
                            Click to Upload
                          </button>
                          <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 mt-2">
                            CSV, XLSX or XLS (Max 10MB)
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className={`border-2 rounded-2xl p-4 shadow-xl transition-all ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-teal-300'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 flex-1">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-white/10 text-emerald-400' : 'bg-teal-50 text-teal-600'}`}>
                            <FileText className="w-6 h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-black text-xs uppercase tracking-widest truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{uploadedFile.name}</p>
                            <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-1">
                              {(uploadedFile.size / 1024).toFixed(2)} KB
                            </p>
                            {uploading && (
                              <div className={`w-full h-1.5 rounded-full mt-3 overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
                                <div
                                  className="h-full bg-teal-500 rounded-full transition-all duration-300"
                                  style={{ width: '75%' }}
                                >
                                  <div className="w-full h-full bg-teal-400 rounded-full animate-pulse"></div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveFile}
                          disabled={uploading}
                          className={`p-2 rounded-full transition-all ml-2 disabled:opacity-50 ${isDark ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 z-10 p-6 pt-4 border-t transition-all flex justify-end ${isDark ? 'bg-[#111827] border-white/10' : 'bg-[#E8F5F0] border-teal-200'}`}>
          <button
            onClick={handleSave}
            disabled={!uploadedFile || uploading}
            className={`px-10 py-4 text-white rounded-2xl transition-all font-black text-xs uppercase tracking-[0.2em] shadow-xl active:scale-95 disabled:opacity-30 disabled:grayscale disabled:scale-100 disabled:cursor-not-allowed flex items-center space-x-3`}
            style={{ backgroundColor: uploadedFile && !uploading ? currentTheme.color : undefined }}
          >
            {uploading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <span>{uploading ? 'Processing...' : 'Save Leads'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default BulkLeadUploadModal;